import { Input } from "@components/";
import { useProductSearch } from "./useProductSearch";
import type { ProductResult, SearchItem } from "./interfaces";

interface ProductSearchProps {
  onSelect: (product: ProductResult) => void;
  placeholder?: string;
}

export function ProductSearch({
  onSelect,
  placeholder = "Busque por nome, código ou SKU",
}: ProductSearchProps) {
  const {
    query,
    results,
    open,
    loading,
    error,
    highlight,
    onSearchChange,
    onKeyDown,
    onBlur,
    onSelect: handleSelectFromSearch,
    handleClearClick,
  } = useProductSearch({
    onSelect: (item: SearchItem) => {
      onSelect(item as ProductResult);
    },
    minChars: 2,
    debounceMs: 300,
  });

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Input
            label="Buscar produto"
            placeholder={placeholder}
            value={query}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            autoComplete="off"
          />

          {query && (
            <button
              type="button"
              onClick={handleClearClick}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-2">
          <div className="text-sm text-gray-500">Buscando produtos...</div>
        </div>
      )}

      {error && open && (
        <div className="absolute top-full left-0 right-0 bg-white border border-red-200 rounded-md shadow-lg z-10 p-2">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {open && !loading && !error && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
          {results.map((item: SearchItem, index: number) => {
            const product = item as ProductResult;
            return (
              <div
                key={item.id}
                className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  index === highlight ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
                onClick={(e) => {
                  e.preventDefault(); // Prevenir comportamento padrão
                  e.stopPropagation(); // Parar propagação do evento
                  handleSelectFromSearch(item);
                }}
              >
                <div className="font-medium text-sm">{item.name}</div>
                {product.custom_object_fields?.codigo_do_produto != null && (
                  <div className="text-xs text-gray-500 mt-1">
                    Código:{" "}
                    {String(product.custom_object_fields.codigo_do_produto)}
                  </div>
                )}
                {product.custom_object_fields?.descricao_do_produto != null && (
                  <div className="text-xs text-gray-500 mt-1">
                    {String(product.custom_object_fields.descricao_do_produto)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open &&
        !loading &&
        !error &&
        results.length === 0 &&
        query.length >= 2 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-2">
            <div className="text-sm text-gray-500">
              Nenhum produto encontrado
            </div>
          </div>
        )}
    </div>
  );
}
